// frontend/src/components/ABAnalyticsTab.jsx
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { getABAnalytics } from "../api/variants";
import Spinner from "./Spinner";

const BAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#db2777", "#16a34a"];

/**
 * ABAnalyticsTab — per-variant scan counts, percentages, and a chi-squared
 * significance read-out for a code's A/B test (P14-G8).
 *
 * Self-fetches on mount. Renders an empty hint when the code has no variants.
 *
 * @param {object} props
 * @param {number} props.codeId
 */
function ABAnalyticsTab({ codeId }) {
  const [data, setData] = useState(null); // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(false);
    getABAnalytics(codeId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [codeId]);

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-500">
        Couldn’t load A/B analytics.
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 flex justify-center">
        <Spinner size={24} className="text-blue-500" />
      </div>
    );
  }

  if (!data.variants || data.variants.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center space-y-1">
        <p className="text-sm font-semibold text-gray-700">
          No A/B test configured
        </p>
        <p className="text-xs text-gray-500">
          Enable A/B testing from the code editor to split traffic between two
          destinations.
        </p>
      </div>
    );
  }

  const chartData = data.variants.map((v) => ({
    name: v.name,
    scans: v.scan_count,
    percentage: v.percentage,
  }));

  return (
    <div className="space-y-4">
      {/* Bar chart — scans per variant */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            Variant performance
          </span>
          <span className="text-xs text-gray-400">
            {data.total_scans} total scan{data.total_scans === 1 ? "" : "s"}
          </span>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 56)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 0, right: 32, bottom: 0, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "#6b7280" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={80}
                tick={{ fontSize: 12, fill: "#374151" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: "#eff6ff" }}
                formatter={(value, key) =>
                  key === "scans" ? [value, "Scans"] : value
                }
              />
              <Bar dataKey="scans" radius={[0, 4, 4, 0]} maxBarSize={32}>
                {chartData.map((entry, i) => (
                  <Cell key={entry.name} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per-variant table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {["Variant", "Weight", "Scans", "Share", "Destination"].map((c) => (
                <th
                  key={c}
                  className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.variants.map((v, i) => (
              <tr key={v.id}>
                <td className="px-4 py-2.5 text-gray-800 font-medium">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }}
                    />
                    {v.name}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-gray-600 tabular-nums">{v.weight}%</td>
                <td className="px-4 py-2.5 text-gray-600 tabular-nums">{v.scan_count}</td>
                <td className="px-4 py-2.5 text-gray-600 tabular-nums">{v.percentage}%</td>
                <td className="px-4 py-2.5 text-gray-500 max-w-xs truncate" title={v.destination}>
                  {v.destination}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Significance read-out */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm">
        {data.chi_squared === null ? (
          <p className="text-gray-500">
            Not enough scans yet to assess whether the split is statistically
            meaningful.
          </p>
        ) : data.significant ? (
          <p className="text-emerald-700">
            <span className="font-semibold">Statistically significant</span>{" "}
            (χ² = {data.chi_squared}, df = {data.degrees_of_freedom}, p &lt; 0.05)
            — the observed split deviates meaningfully from the configured
            weights.
          </p>
        ) : (
          <p className="text-gray-600">
            <span className="font-semibold">Not yet significant</span> (χ² ={" "}
            {data.chi_squared}, df = {data.degrees_of_freedom}) — keep the test
            running to gather more scans.
          </p>
        )}
      </div>
    </div>
  );
}

ABAnalyticsTab.propTypes = {
  codeId: PropTypes.number.isRequired,
};

export default ABAnalyticsTab;
